// import components
import showError from 'components/shared/showError';

// import hooks
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

// import queries & mutations
import { mutations, queryKeys } from 'RestAPIs/rapini';
import { getReport } from 'components/admin-portal/Procedures/Reports/CollectedReports/hooks/useGetAccessedReports';
import {
  loadReport,
  useGetGeneratedReport
} from 'components/admin-portal/Procedures/Reports/CollectedReports/hooks/useGetGeneratedReport';
import { SERVICEDESK_REPORTS } from 'components/admin-portal/Procedures/Reports/type';

// import types
import type { AxiosError } from 'axios';
import type { ExcelFinding } from 'components/shared/ExcelImport';
import type { GeneratedReport } from 'RestAPIs/reports/generated';
import type { HoursBalanceImportOptions } from 'generated';
import type {
  HoursBalanceExcelRow,
  HoursBalanceImportProgress,
  HoursBalanceImportSummary
} from './HoursBalanceImport.types';

// import utils
import { StatusEnum } from 'RestAPIs/reports/generated';
import {
  BLOCK_SIZE,
  chunk,
  toExcelFinding,
  toExecuteRow,
  toValidateRow
} from './HoursBalanceImport.utils';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

const isSameMonthReport = (report: GeneratedReport, month: Date) => {
  const paramValue = report.parameters?.valid_from;
  if (!paramValue) return false;
  const paramDate =
    paramValue instanceof Date ? paramValue : new Date(paramValue);
  return (
    paramDate.getFullYear() === month.getFullYear() &&
    paramDate.getMonth() === month.getMonth()
  );
};

// Downloads the existing "Urensaldi template" report for a chosen month: request
// generation, poll the generic generated-reports list until that exact month's row
// is DONE, then reuse the existing download helper.
export const useHoursBalanceTemplateDownload = () => {
  const queryClient = useQueryClient();
  const [requestedMonth, setRequestedMonth] = useState<Date | null>(null);
  // Which reports already existed when the template was requested: the freshly
  // generated one is the row that wasn't in that snapshot. Matching on "DONE for this
  // month" alone would hand back an older export of the same month, from before the
  // corrections the operator just made.
  const [knownFileIds, setKnownFileIds] = useState<Set<number> | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const { data: generatedReports } = useGetGeneratedReport();

  useEffect(() => {
    if (!requestedMonth) return undefined;

    const pollInterval = setInterval(() => {
      queryClient.invalidateQueries(['generated']);
    }, POLL_INTERVAL_MS);
    const timeout = setTimeout(() => {
      setRequestedMonth(null);
      setHasTimedOut(true);
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [requestedMonth, queryClient]);

  useEffect(() => {
    if (!requestedMonth || !knownFileIds) return;
    const match = generatedReports?.find(
      (report) =>
        report.reportName === SERVICEDESK_REPORTS.HOUR_BALANCE_REPORT &&
        report.status === StatusEnum.DONE &&
        isSameMonthReport(report, requestedMonth) &&
        !knownFileIds.has(report.fileId)
    );
    if (match) {
      loadReport(match);
      setRequestedMonth(null);
    }
  }, [generatedReports, requestedMonth, knownFileIds]);

  const downloadTemplate = async (month: Date) => {
    setHasTimedOut(false);
    // Snapshot before the request, so a fast backend can't finish first.
    setKnownFileIds(
      new Set((generatedReports ?? []).map(({ fileId }) => fileId))
    );
    try {
      await getReport({
        baseType: 'servicedesk',
        reportTypes: SERVICEDESK_REPORTS.HOUR_BALANCE_REPORT,
        requestParameters: { valid_from: month }
      });
      setRequestedMonth(month);
    } catch (error) {
      showError(error as AxiosError);
    }
  };

  // The snapshot in downloadTemplate is only meaningful once the list has loaded;
  // an empty snapshot would match an older DONE export of the same month.
  return {
    downloadTemplate,
    isReady: generatedReports !== undefined,
    isPolling: !!requestedMonth,
    hasTimedOut
  };
};

export interface HoursBalanceImportRunState {
  isRunning: boolean;
  progress: HoursBalanceImportProgress | null;
  findings: ExcelFinding[];
  summary: HoursBalanceImportSummary | null;
  partialProcessed: number | null;
}

const INITIAL_RUN_STATE: HoursBalanceImportRunState = {
  isRunning: false,
  progress: null,
  findings: [],
  summary: null,
  partialProcessed: null
};

// The validate-then-execute block loop: every block is validated up front, and only
// once all blocks together yield zero findings does the execute phase start. Both
// phases share one continuous progress count across all 2 * blocks requests.
export const useHoursBalanceImportRun = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: validateBlock } =
    mutations.useSecureAdminProceduresServicedeskHoursBalanceValidate();
  const { mutateAsync: executeBlock } =
    mutations.useSecureAdminProceduresServicedeskHoursBalanceExecute();

  const [state, setState] =
    useState<HoursBalanceImportRunState>(INITIAL_RUN_STATE);

  const reset = () => setState(INITIAL_RUN_STATE);

  const runBlocks = async (
    rows: HoursBalanceExcelRow[],
    options: HoursBalanceImportOptions
  ) => {
    const month = rows[0].month;
    const blocks = chunk(rows, BLOCK_SIZE);
    const total = blocks.length * 2;
    let current = 0;

    setState({
      ...INITIAL_RUN_STATE,
      isRunning: true,
      progress: { current, total }
    });

    const dutyLineMngIdByRow = new Map<number, number>();
    const validateFindings: ExcelFinding[] = [];

    try {
      for (const block of blocks) {
        const response = await validateBlock({
          month,
          rows: block.map(toValidateRow)
        });
        response.rows.forEach((validatedRow) => {
          if (validatedRow.dutyLineMngId != null) {
            dutyLineMngIdByRow.set(
              validatedRow.rowNumber,
              validatedRow.dutyLineMngId
            );
          }
        });
        validateFindings.push(...response.findings.map(toExcelFinding));
        current += 1;
        setState((previous) => ({ ...previous, progress: { current, total } }));
      }
    } catch (error) {
      setState((previous) => ({
        ...previous,
        isRunning: false
      }));
      showError(error as AxiosError);
      return;
    }

    if (validateFindings.length > 0) {
      setState((previous) => ({
        ...previous,
        isRunning: false,
        findings: validateFindings
      }));
      return;
    }

    let runId: number | undefined;
    let rowsProcessed = 0;

    for (let index = 0; index < blocks.length; index += 1) {
      const block = blocks[index];
      const executeRows = block.map((row) =>
        toExecuteRow(row, dutyLineMngIdByRow.get(row.rowNumber) as number)
      );

      try {
        const response = await executeBlock({
          runId,
          month,
          lastBlock: index === blocks.length - 1,
          options,
          rows: executeRows
        });

        runId = response.runId ?? runId;
        current += 1;

        if (response.findings.length > 0) {
          setState((previous) => ({
            ...previous,
            isRunning: false,
            findings: response.findings.map(toExcelFinding),
            partialProcessed: rowsProcessed
          }));
          return;
        }

        rowsProcessed += block.length;

        setState({
          isRunning: index !== blocks.length - 1,
          progress: { current, total },
          findings: [],
          summary: {
            updatedCount: response.updatedCount,
            skippedCount: response.skippedCount,
            clearedCount: response.clearedCount ?? null,
            status: response.status
          },
          partialProcessed: null
        });
      } catch (error) {
        setState((previous) => ({
          ...previous,
          isRunning: false,
          partialProcessed: rowsProcessed
        }));
        showError(error as AxiosError);
        return;
      }
    }
  };

  // Even a run that stops early has already written rows and logged a run, so the
  // Historie tab has to be invalidated on every exit, not just the happy one.
  const run = async (
    rows: HoursBalanceExcelRow[],
    options: HoursBalanceImportOptions
  ) => {
    try {
      await runBlocks(rows, options);
    } finally {
      queryClient.invalidateQueries(
        queryKeys.secureAdminProceduresServicedeskRuns()
      );
    }
  };

  return { ...state, run, reset };
};
